import type {
  CreateTenantPayload,
  RenewTenantPayload,
  Tenant,
  TenantProvisionCredentials,
  TenantsListResponse,
  UpdateTenantPayload,
} from '../types/tenant.types';
import { apiFetch } from './client';
import { parseJsonResponse } from './parseResponse';

function asObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function normalizeId(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function extractTenantId(body: unknown): string | null {
  const root = asObject(body);
  if (!root) return null;

  const data = asObject(root.data);
  const tenant = asObject(root.tenant);
  const candidates: unknown[] = [
    root.id,
    root.tenant_id,
    data?.id,
    data?.tenant_id,
    tenant?.id,
    tenant?.tenant_id,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeId(candidate);
    if (normalized) return normalized;
  }
  return null;
}

function extractTenantSlug(body: unknown): string | null {
  const root = asObject(body);
  if (!root) return null;

  const data = asObject(root.data);
  const tenant = asObject(root.tenant);
  const dataTenant = asObject(data?.tenant);

  const candidates: unknown[] = [data?.slug, tenant?.slug, dataTenant?.slug, root.slug];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
}

function extractProvisionCredentials(body: unknown): TenantProvisionCredentials | null {
  const root = asObject(body);
  if (!root) return null;

  const data = asObject(root.data);
  const tenant = asObject(root.tenant);
  const admin = asObject(root.admin);
  const dataAdmin = asObject(data?.admin);
  const tenantAdmin = asObject(tenant?.admin);
  const dataTenant = asObject(data?.tenant);

  const pools: Array<Record<string, unknown> | null> = [
    root,
    data,
    tenant,
    admin,
    dataAdmin,
    tenantAdmin,
    dataTenant,
  ];

  for (const pool of pools) {
    if (!pool) continue;
    const email =
      (typeof pool.email === 'string' && pool.email.trim()) ||
      (typeof pool.admin_email === 'string' && pool.admin_email.trim()) ||
      undefined;
    const username =
      (typeof pool.username === 'string' && pool.username.trim()) ||
      (typeof pool.admin_username === 'string' && pool.admin_username.trim()) ||
      undefined;
    const password =
      (typeof pool.password === 'string' && pool.password.trim()) ||
      (typeof pool.admin_password === 'string' && pool.admin_password.trim()) ||
      undefined;

    if (email || username || password) {
      return { email, username, password };
    }
  }

  return null;
}

/**
 * GET /platform/tenants?per_page=&page=&search=&status=&plan_id=
 * Response: { success, data: Tenant[], meta: { current_page, per_page, total, last_page } }
 */
export async function fetchTenantsList(
  page: number,
  perPage = 15,
  opts?: { search?: string; status?: string; plan_id?: number },
): Promise<
  | { ok: true; list: TenantsListResponse }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const qs = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
  });
  if (opts?.search) qs.set('search', opts.search);
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.plan_id) qs.set('plan_id', String(opts.plan_id));

  const res = await apiFetch(`/platform/tenants?${qs.toString()}`);
  const out = await parseJsonResponse<Tenant[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }

  const meta = out.meta ?? { current_page: 1, per_page: perPage, total: 0, last_page: 1 };
  return {
    ok: true,
    list: {
      data: Array.isArray(out.data) ? out.data : [],
      current_page: meta.current_page,
      per_page: meta.per_page,
      total: meta.total,
      last_page: meta.last_page,
    },
  };
}

/**
 * GET /platform/tenants/:id
 */
export async function fetchTenant(
  tenantId: string | number,
): Promise<
  | { ok: true; tenant: Tenant }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}`);
  const out = await parseJsonResponse<Tenant>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, tenant: out.data };
}

/**
 * POST /platform/tenants
 * Response (201): { success, message, data: TenantResource }
 */
export async function createTenant(
  payload: CreateTenantPayload,
): Promise<
  | {
      ok: true;
      tenantId: string | null;
      tenantSlug: string | null;
      credentials: TenantProvisionCredentials | null;
    }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch('/platform/tenants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return {
    ok: true,
    tenantId: extractTenantId(out.data),
    tenantSlug: extractTenantSlug(out.data),
    credentials: extractProvisionCredentials(out.data),
  };
}

/**
 * PUT /platform/tenants/:id
 */
export async function updateTenant(
  tenantId: string | number,
  payload: UpdateTenantPayload,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

/**
 * POST /platform/tenants/:id/suspend
 */
export async function suspendTenant(
  tenantId: string | number,
): Promise<
  | { ok: true; tenant: Tenant }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/suspend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const out = await parseJsonResponse<Tenant>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, tenant: out.data };
}

/**
 * POST /platform/tenants/:id/activate
 */
export async function activateTenant(
  tenantId: string | number,
): Promise<
  | { ok: true; tenant: Tenant }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const out = await parseJsonResponse<Tenant>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, tenant: out.data };
}

/**
 * POST /platform/tenants/:id/renew
 */
export async function renewTenant(
  tenantId: string | number,
  payload: RenewTenantPayload,
): Promise<
  | { ok: true; tenant: Tenant }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/renew`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<Tenant>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, tenant: out.data };
}

/**
 * POST /platform/tenants/:id/domains
 */
export async function addTenantDomain(
  tenantId: string | number,
  domain: string,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/domains`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain: domain.trim() }),
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

/**
 * DELETE /platform/tenants/:id/domains/:domainId
 */
export async function deleteTenantDomain(
  tenantId: string | number,
  domainId: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(
    `/platform/tenants/${encodeURIComponent(String(tenantId))}/domains/${encodeURIComponent(String(domainId))}`,
    { method: 'DELETE' },
  );
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

/**
 * POST /platform/tenants/:id/migrate
 */
export async function migrateTenant(
  tenantId: string | number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/migrate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

/**
 * POST /platform/tenants/:id/seed
 */
export async function seedTenant(
  tenantId: string | number,
  payload?: {
    email?: string;
    password?: string;
    username?: string;
    admin_email?: string;
    admin_password?: string;
    admin_username?: string;
    phone?: string;
  },
): Promise<
  | { ok: true; credentials: TenantProvisionCredentials | null; tenantSlug: string | null }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}/seed`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload ?? {}),
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return {
    ok: true,
    credentials: extractProvisionCredentials(out.data),
    tenantSlug: extractTenantSlug(out.data),
  };
}

/**
 * DELETE /platform/tenants/:id
 */
export async function deleteTenant(
  tenantId: string | number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/tenants/${encodeURIComponent(String(tenantId))}`, {
    method: 'DELETE',
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}

/**
 * Platform admin SSO into tenant app as atelier owner.
 * POST /platform/tenants/{id}/login-as
 */
export async function loginAsTenant(
  tenantId: string | number,
): Promise<
  | { ok: true; handoffUrl: string; loginUrl?: string }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(
    `/platform/tenants/${encodeURIComponent(String(tenantId))}/login-as`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );
  const out = await parseJsonResponse<{
    handoff_url?: string;
    login_url?: string;
  }>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  const handoffUrl = out.data?.handoff_url?.trim();
  if (!handoffUrl) {
    return { ok: false, message: 'لم يُرجع الخادم رابط الدخول' };
  }
  return {
    ok: true,
    handoffUrl,
    loginUrl: out.data?.login_url,
  };
}

/**
 * @deprecated Use suspendTenant / activateTenant instead.
 * Kept for backward compat — calls the correct endpoint based on current tenant status.
 */
export async function toggleTenantActive(
  tenantId: string | number,
  currentlyActive?: boolean,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const endpoint = currentlyActive === false ? 'activate' : 'suspend';
  const res = await apiFetch(
    `/platform/tenants/${encodeURIComponent(String(tenantId))}/${endpoint}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    },
  );
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}
